import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRightLeft, ShieldCheck } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";

import { useToast } from "./ui/use-toast";
import ConfirmTransfer from "./ConfirmTransfer";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { FormItem, FormMessage } from "./ui/form";
import useReactForm from "../../hooks/useForm";
import "../custom-styles.css";
import validate from "../../helpers/validate";
import {
  findWalletByNumber,
  formatAmount,
  getWalletBalance,
  getWalletBlockedBalance,
  getWalletLabel,
  getWalletNumber,
  normalizeWalletList,
  sanitizeWalletNumber,
} from "../../helpers/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { getUser, walletTransfer, getUserId, getBusinessWallet } from "../../services/index";
import { BalanceContainer, BalanceMain } from "./Styles";
import {
  TransferBadge,
  TransferDialogBody,
  TransferDialogContent,
  TransferFieldGrid,
  TransferFooter,
  TransferFooterNote,
  TransferHeader,
  TransferSection,
  TransferSectionDescription,
  TransferSectionHeader,
  TransferSectionTitle,
} from "./TransferFormStyles";

const hasStoredAuth = () => Boolean(localStorage.getItem("access") || localStorage.getItem("refresh"));

const defaultConfirmationDetails = {
  success: null,
  error: false,
  errorMessage: null,
};

const InitiateTransfer = ({
  open,
  handleClose,
  businesses,
  isBusinessTransfer,
  isLoadingBusiness,
}: {
  open: boolean;
  handleClose: () => void;
  businesses?: any;
  isBusinessTransfer?: boolean;
  isLoadingBusiness?: boolean;
}) => {
  const methods = useForm();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { values, errors, setValues, handleChange, reset } = useReactForm(validate);

  const [selectedCurrency, setSelectedCurrency] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState<any>(defaultConfirmationDetails);
  const receiverWalletNumber = sanitizeWalletNumber(values?.receiver_wallet_number);

  const handleUserId = async (walletNumber: string | number) => {
    try {
      const normalizedWalletNumber = sanitizeWalletNumber(String(walletNumber));
      return await getUserId(normalizedWalletNumber);
    } catch (error: any) {
      setConfirmationDetails({
        error: true,
        errorMessage:
          error?.response?.data?.message ?? "An error occured whilst fetching receiver details",
      });
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.response?.data?.message,
      });
    }
  };

  const {
    data: businessWalletData,
    isLoading: isLoadingBusinessWalletData,
  } = useQuery({
    queryKey: ["businessWallet", selectedBusiness],
    queryFn: () => getBusinessWallet(selectedBusiness as string),
    enabled: !!selectedBusiness,
  });

  const { isLoading: walletIsLoading, data: userData } = useQuery({
    queryKey: ["userDetails"],
    queryFn: () => getUser(),
    enabled: hasStoredAuth(),
  });

  const wallets = userData?.wallets ?? [];
  const walletOptions = isBusinessTransfer ? normalizeWalletList(businessWalletData) : wallets;

  useEffect(() => {
    if (!isBusinessTransfer || selectedBusiness || !businesses?.length) {
      return;
    }

    const preferredBusiness =
      businesses.find((business: any) => business.is_default) ?? businesses[0];

    if (preferredBusiness?.id) {
      const businessId = String(preferredBusiness.id);
      setSelectedBusiness(businessId);
      setValues((prevValues: any) => ({
        ...prevValues,
        business_id: businessId,
      }));
    }
  }, [businesses, isBusinessTransfer, selectedBusiness, setValues]);

  const {
    data: userId,
    isFetching: userIdIsLoading,
    isError: userIdIsError,
    isSuccess: userIdSuccess,
  } = useQuery({
    queryKey: ["userId", receiverWalletNumber],
    queryFn: () => handleUserId(receiverWalletNumber),
    enabled: openConfirm && receiverWalletNumber.length === 16,
  });

  useEffect(() => {
    if (userIdSuccess) {
      setConfirmationDetails({ success: userId });
    }
  }, [userIdSuccess, userIdIsError, toast, userId, openConfirm]);

  const transferMutation = useMutation({
    mutationKey: ["transfer"],
    mutationFn: (payload: any) => walletTransfer(payload),
    onSuccess: async () => {
      toast({
        description: "Wallet transfer was successful",
      });
      queryClient.invalidateQueries({ queryKey: ["transferHistory"] });
      queryClient.invalidateQueries({ queryKey: ["businessHistory"] });
      queryClient.invalidateQueries({ queryKey: ["businessWallet"] });
      queryClient.refetchQueries({ queryKey: ["transferHistoryQueryKey"] });
      handleModalClose();
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.response?.data.message ?? "Wallet transfer failed",
      });
    },
  });

  const walletSelectDisabled =
    walletIsLoading || isLoadingBusinessWalletData || (isBusinessTransfer && !selectedBusiness);

  const walletPlaceholder = isBusinessTransfer && !selectedBusiness
    ? "choose a business first"
    : walletIsLoading || isLoadingBusinessWalletData
    ? "loading wallets..."
    : "select wallet";

  const handleWalletSelect = (walletNumber: string) => {
    const selectedWallet = findWalletByNumber(walletOptions, walletNumber);

    setSelectedCurrency(selectedWallet);
    setValues((prevValues: any) => ({
      ...prevValues,
      sender_wallet_number: walletNumber,
    }));
  };

  const handleBusinessSelect = (businessId: string) => {
    setSelectedBusiness(businessId);
    setSelectedCurrency(null);
    setValues((prevValues: any) => ({
      ...prevValues,
      business_id: businessId,
      sender_wallet_number: "",
    }));
  };

  const handleTransfer = () => {
    transferMutation.mutate({
      ...values,
      receiver_wallet_number: receiverWalletNumber,
    });
  };

  const handleModalClose = () => {
    handleClose();
    setOpenConfirm(false);
    reset();
    setSelectedCurrency(null);
    setSelectedBusiness(null);
    setConfirmationDetails(defaultConfirmationDetails);
  };

  const handleConfirmClose = () => {
    setOpenConfirm(false);
    setConfirmationDetails(defaultConfirmationDetails);
  };

  const formatWalletNumber = (walletNumber: string | undefined | null): string => {
    if (!walletNumber) return "";
    return sanitizeWalletNumber(walletNumber)
      .slice(0, 16)
      .replace(/(\d{4})/g, "$1 ")
      .trim();
  };

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="DialogOverlay" />
        <TransferDialogContent className="DialogContent">
          <TransferDialogBody>
            <TransferHeader>
              <div className="flex-1">
                <TransferBadge>
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Secure transfer
                </TransferBadge>
                <Dialog.Title className="DialogTitle mt-3">Transfer Money</Dialog.Title>
                <Dialog.Description className="DialogDescription">
                  Choose the wallet, amount, and recipient below. You&apos;ll review everything
                  before the transfer is sent.
                </Dialog.Description>
              </div>

              <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 sm:flex">
                <ArrowRightLeft className="h-4 w-4 text-rose-500" />
                Review before sending
              </div>
            </TransferHeader>

            <FormProvider {...methods}>
              <div className="flex flex-col gap-4">
                {isBusinessTransfer && (
                  <TransferSection>
                    <TransferSectionHeader>
                      <div>
                        <TransferSectionTitle>Business source</TransferSectionTitle>
                        <TransferSectionDescription>
                          Pick the business profile that will fund this transfer.
                        </TransferSectionDescription>
                      </div>
                    </TransferSectionHeader>

                    <FormItem className="space-y-3">
                      <Label>Select business*</Label>
                      <Select
                        value={selectedBusiness || undefined}
                        disabled={isLoadingBusiness}
                        onValueChange={handleBusinessSelect}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={isLoadingBusiness ? "loading businesses..." : "select business"}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {businesses?.map(({ id, name }: { id: string; name: string }) => (
                              <SelectItem key={id} value={id}>
                                {name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  </TransferSection>
                )}

                <TransferSection>
                  <TransferSectionHeader>
                    <div>
                      <TransferSectionTitle>Funding wallet</TransferSectionTitle>
                      <TransferSectionDescription>
                        Choose the wallet that will be debited for this transfer.
                      </TransferSectionDescription>
                    </div>
                  </TransferSectionHeader>

                  <FormItem className="space-y-3">
                    <Label>Select wallet to transfer*</Label>
                    <Select
                      value={values?.sender_wallet_number || undefined}
                      disabled={walletSelectDisabled}
                      onValueChange={handleWalletSelect}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={walletPlaceholder} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          {walletOptions?.map((wallet: any, index: number) => {
                            const walletNumber = getWalletNumber(wallet);

                            if (!walletNumber) {
                              return null;
                            }

                            return (
                              <SelectItem key={walletNumber} value={walletNumber}>
                                {getWalletLabel(wallet, index)}
                              </SelectItem>
                            );
                          })}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </FormItem>

                  {selectedCurrency && (
                    <BalanceContainer>
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-rose-700/80">Available balance</p>
                        <BalanceMain>
                          {selectedCurrency.currency} {getWalletBalance(selectedCurrency)}
                        </BalanceMain>
                        {getWalletBlockedBalance(selectedCurrency) > 0 && (
                          <p className="text-xs text-rose-600/60">
                            In trade: {selectedCurrency.currency} {formatAmount(getWalletBlockedBalance(selectedCurrency))}
                          </p>
                        )}
                      </div>

                      <div className="rounded-full bg-white/80 px-3 py-1 text-xs font-semibold tracking-wide text-slate-600 shadow-sm">
                        {selectedCurrency.currency_value || selectedCurrency.currency}
                      </div>
                    </BalanceContainer>
                  )}
                </TransferSection>

                <TransferSection>
                  <TransferSectionHeader>
                    <div>
                      <TransferSectionTitle>Transfer details</TransferSectionTitle>
                      <TransferSectionDescription>
                        Add the amount and the receiver&apos;s wallet number.
                      </TransferSectionDescription>
                    </div>
                  </TransferSectionHeader>

                  <TransferFieldGrid>
                    <FormItem className="space-y-3">
                      <Label>Amount to transfer*</Label>
                      <Input
                        name="amount"
                        placeholder="2000"
                        onChange={handleChange}
                        value={values?.amount || ""}
                        type="number"
                        min={1}
                      />
                      {errors?.amount && <FormMessage>{errors?.amountErrorMessage}</FormMessage>}
                    </FormItem>

                    <FormItem className="space-y-3">
                      <Label>Receiver&apos;s wallet number*</Label>
                    <Input
                      type="text"
                      name="receiver_wallet_number"
                      inputMode="numeric"
                      onChange={(event) =>
                        setValues((prevValues: any) => ({
                          ...prevValues,
                          receiver_wallet_number: sanitizeWalletNumber(event.target.value),
                        }))
                        }
                        value={formatWalletNumber(values?.receiver_wallet_number || "")}
                        maxLength={19}
                      />
                      {errors?.receiver_wallet_number && (
                        <FormMessage>{errors?.receiverWalletNumberErrorMessage}</FormMessage>
                      )}
                    </FormItem>
                  </TransferFieldGrid>

                  <FormItem className="space-y-3">
                    <Label>Description</Label>
                    <Input
                      name="description"
                      placeholder="description"
                      onChange={handleChange}
                      value={values?.description || ""}
                    />
                    {errors?.description && <FormMessage>{errors?.description}</FormMessage>}
                  </FormItem>
                </TransferSection>
              </div>
            </FormProvider>

            <TransferFooter>
              <TransferFooterNote>
                You&apos;ll get one final confirmation before the transfer is completed.
              </TransferFooterNote>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleModalClose}
                  className="rounded-2xl border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  Cancel
                </Button>

                <Dialog.Close asChild>
                    <Button
                      type="button"
                      disabled={
                        !values?.sender_wallet_number ||
                        receiverWalletNumber.length !== 16 ||
                        (values?.amount !== undefined &&
                          values?.amount !== null &&
                          parseFloat(values?.amount) < 0) ||
                      Boolean(Object.values(errors).length) ||
                      transferMutation.isPending
                    }
                    onClick={() => setOpenConfirm(true)}
                    className="rounded-2xl bg-gradient-to-r from-rose-500 to-red-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-rose-500/20 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-rose-100"
                  >
                    {transferMutation.isPending ? "Loading..." : "Review transfer"}
                  </Button>
                </Dialog.Close>
              </div>
            </TransferFooter>
          </TransferDialogBody>
        </TransferDialogContent>
      </Dialog.Portal>

      <ConfirmTransfer
        open={openConfirm}
        handleClose={handleModalClose}
        handleConfirmClose={handleConfirmClose}
        handleConfirm={handleTransfer}
        values={values}
        selectedCurrency={selectedCurrency}
        transferMutation={transferMutation}
        confirmationDetails={confirmationDetails}
        isLoading={userIdIsLoading}
      />
    </Dialog.Root>
  );
};

export default InitiateTransfer;
